/**
 * SmartDeleteModal
 * ----------------
 * A one-click "wrong entry" correction UI powered by
 * `/api/smart-delete/preview` and `/api/smart-delete/apply`.
 *
 * Behaviour
 *   1. On open → auto-runs preview → shows the cascade plan (which
 *      rows will be deleted / reduced, and stock before/after).
 *   2. User picks a reason (dropdown + optional detail).
 *   3. Confirm → server re-runs the plan with fresh stock, applies it,
 *      writes an audit log entry, and returns.
 *
 * Callers pass `txnType`, `txnId`, and (optionally) `onDeleted` to
 * refresh their tables.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Warning, Trash, X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REASONS = [
  "Wrong quantity entered",
  "Wrong part number",
  "Duplicate entry",
  "Wrong supplier / customer",
  "Test entry / trial",
  "Other",
];

export default function SmartDeleteModal({ txnType, txnId, open, onClose, onDeleted }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [reasonDetail, setReasonDetail] = useState("");
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !txnId) return;
    setLoading(true);
    setPreview(null);
    setReason(REASONS[0]);
    setReasonDetail("");
    axios.post(`${API}/smart-delete/preview`, { txn_type: txnType, txn_id: txnId })
      .then(r => setPreview(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Preview failed"))
      .finally(() => setLoading(false));
  }, [open, txnType, txnId]);

  const apply = async () => {
    if (!reason) return toast.error("Please pick a reason");
    if (reason === "Other" && !reasonDetail.trim()) return toast.error("Add a short note for 'Other'");
    setApplying(true);
    try {
      const r = await axios.post(`${API}/smart-delete/apply`, {
        txn_type: txnType,
        txn_id: txnId,
        reason,
        reason_detail: reasonDetail,
        confirmed_cascade_ids: (preview?.plan || []).map(p => p.txn_id),
      });
      toast.success(r.data.message);
      onClose?.();
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose} data-testid="smart-delete-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[92vh] overflow-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-[#B91C1C] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trash size={18} weight="fill" />
            <div>
              <h3 className="font-black text-base leading-tight">Smart Delete — Wrong Entry Correction</h3>
              <p className="text-[10px] text-white/80 mt-0.5">
                Reverses only what's needed. Downstream stock stays untouched if possible.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {loading && <div className="py-6 text-center text-[#64748B] text-sm">Analysing impact…</div>}

          {preview && !loading && (
            <>
              {/* Selected txn */}
              <div className="rounded border border-[#FDA4AF] bg-[#FFF1F2] p-3 text-[11px]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#9F1239] mb-1">Selected Transaction</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {Object.entries(preview.selected || {})
                    .filter(([k]) => k !== "id" && k !== "type")
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-[#64748B] uppercase text-[9px]">{k.replace(/_/g, " ")}</span>
                        <span className="font-mono font-semibold text-[#0F172A]">{String(v ?? "—")}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Warning */}
              {preview.warning && (
                <div className={`rounded p-2 text-[11px] flex gap-2 items-start ${
                  preview.can_proceed ? "bg-[#FEF3C7] text-[#78350F] border border-[#FBBF24]"
                                       : "bg-[#FEE2E2] text-[#7F1D1D] border border-[#F87171]"
                }`}>
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                  <span>{preview.warning}</span>
                </div>
              )}

              {/* Cascade plan */}
              <div className="border border-[#E2E8F0] rounded overflow-hidden">
                <div className="px-3 py-2 bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-widest text-[#475569]">
                  Cascade Plan · {preview.plan?.length} step(s)
                </div>
                <table className="w-full text-[11px]" data-testid="cascade-plan-table">
                  <thead className="bg-[#F1F5F9] text-[10px] uppercase text-[#475569]">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold">Stage</th>
                      <th className="px-2 py-1.5 text-left font-semibold">What</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Qty</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Stock Before</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Stock After</th>
                      <th className="px-2 py-1.5 text-center font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {preview.plan?.map((p, i) => (
                      <tr key={i} className="hover:bg-[#F8FAFC]">
                        <td className="px-2 py-1.5 font-semibold text-[#334155]">{p.stage}</td>
                        <td className="px-2 py-1.5 text-[#0F172A]">{p.label}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{p.qty}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{p.stock_before}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-[#065F46]">{p.stock_after}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            p.action === "delete" ? "bg-[#FEE2E2] text-[#991B1B]" :
                            p.action === "reduce" ? "bg-[#FEF3C7] text-[#78350F]" :
                            "bg-[#DBEAFE] text-[#1E40AF]"
                          }`}>{p.action.replace("_", " ")}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Reason */}
              {preview.can_proceed && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] font-semibold text-[#334155]">Reason *</Label>
                    <select value={reason} onChange={e => setReason(e.target.value)}
                      className="w-full min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white mt-0.5"
                      data-testid="smart-delete-reason">
                      {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold text-[#334155]">
                      Detail {reason === "Other" ? "*" : "(optional)"}
                    </Label>
                    <Input value={reasonDetail}
                      onChange={e => setReasonDetail(e.target.value)}
                      placeholder="Add any specifics (audit log)"
                      className="mt-0.5" data-testid="smart-delete-reason-detail" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-[#E2E8F0] flex justify-end gap-2 bg-[#F8FAFC]">
          <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
          {preview?.can_proceed && (
            <Button onClick={apply} disabled={applying}
              className="h-9 bg-[#B91C1C] hover:bg-[#991B1B] text-white gap-1"
              data-testid="smart-delete-confirm">
              <Trash size={14} /> {applying ? "Applying…" : "Confirm Delete"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
